import React from 'react';
import {
  Form, Select, InputNumber, Input, Button, Row, Col, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

const DEFAULT_PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CHEQUE', label: 'Cheque' },
];

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(v || 0);

/**
 * Reusable split-payment Form.List component used across all payment modals.
 *
 * Props:
 *   fieldName       - Form.List field name (default: 'paymentSplits')
 *   totalAmount     - Expected total that splits must match
 *   paymentModes    - Array of { value, label } options (default: CASH/BANK/UPI/CHEQUE)
 *   defaultMode     - Default mode for new rows (default: 'BANK_TRANSFER')
 *   showReference   - Whether to show the reference column (default: true)
 *   referenceModes  - When set, only these modes get an editable reference input; the
 *                     rest show a note instead. Used by the payment flows that route
 *                     through approval, where a bank transfer's UTR does not exist yet
 *                     at record time and is supplied by the approver on the Approve
 *                     action. Leave unset (default) for every flow that records the
 *                     payment and its reference in one step.
 *   referenceHint   - Note shown in place of the input for a mode not in referenceModes
 *   size            - Ant Design size: 'small' | 'middle' (default: 'small')
 */
const PaymentSplitInput = ({
  fieldName = 'paymentSplits',
  totalAmount = 0,
  maxAmount = 0,
  paymentModes = DEFAULT_PAYMENT_MODES,
  defaultMode = 'BANK_TRANSFER',
  showReference = true,
  referenceModes = null,
  referenceHint = 'UTR added by approver',
  size = 'small',
}) => {
  // When maxAmount is set (and totalAmount is 0), use "up to" validation.
  // Otherwise use exact-match validation.
  const isPartialMode = maxAmount > 0 && !totalAmount;
  const effectiveTarget = isPartialMode ? maxAmount : totalAmount;

  return (
    <Form.List
      name={fieldName}
      rules={[
        {
          validator: async (_, splits) => {
            if (!splits || splits.length === 0) {
              throw new Error('Add at least one payment split');
            }
            const total = splits.reduce((s, p) => s + (Number(p?.amount) || 0), 0);
            if (isPartialMode) {
              if (Math.round(total * 100) <= 0) {
                throw new Error('Total must be greater than ₹0');
              }
              if (Math.round(total * 100) > Math.round(maxAmount * 100)) {
                throw new Error(
                  `Split total ${fmt(total)} exceeds maximum ${fmt(maxAmount)}`,
                );
              }
            } else {
              const diff = Math.abs(
                Math.round(total * 100) - Math.round((totalAmount || 0) * 100),
              );
              if (diff > 0) {
                throw new Error(
                  `Split total ${fmt(total)} ≠ expected ${fmt(totalAmount)}`,
                );
              }
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <>
          {fields.map((field, index) => (
            <Row
              key={field.key}
              gutter={8}
              align="middle"
              style={{ marginBottom: 8 }}
            >
              <Col flex="130px">
                <Form.Item
                  name={[field.name, 'mode']}
                  style={{ margin: 0 }}
                  rules={[{ required: true, message: 'Select mode' }]}
                >
                  <Select size={size}>
                    {paymentModes.map((m) => (
                      <Select.Option key={m.value} value={m.value}>
                        {m.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col flex="150px">
                <Form.Item
                  name={[field.name, 'amount']}
                  style={{ margin: 0 }}
                  rules={[
                    { required: true, message: 'Enter amount' },
                    { type: 'number', min: 0.01, message: 'Must be > 0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    style={{ width: '100%' }}
                    size={size}
                    placeholder="Amount (₹)"
                    prefix="₹"
                  />
                </Form.Item>
              </Col>
              {showReference && (
                <Col flex="auto">
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) =>
                      prev?.[fieldName]?.[field.name]?.mode !== cur?.[fieldName]?.[field.name]?.mode
                    }
                  >
                    {({ getFieldValue }) => {
                      const mode = getFieldValue([fieldName, field.name, 'mode']);
                      const captureHere = !referenceModes || referenceModes.includes(mode);
                      if (!captureHere) {
                        return (
                          <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                            {referenceHint}
                          </Text>
                        );
                      }
                      return (
                        <Form.Item
                          name={[field.name, 'reference']}
                          style={{ margin: 0 }}
                        >
                          <Input
                            size={size}
                            placeholder="UTR / Cheque / Ref (optional)"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
              )}
              <Col flex="32px">
                {fields.length > 1 && (
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                )}
              </Col>
            </Row>
          ))}

          {/* Live total row */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const splits = getFieldValue(fieldName) || [];
              const total = splits.reduce(
                (s, p) => s + (Number(p?.amount) || 0),
                0,
              );
              const target = effectiveTarget || 0;
              const remaining = target - total;
              const isValid = isPartialMode
                ? total > 0 && Math.round(total * 100) <= Math.round(target * 100)
                : Math.abs(Math.round(remaining * 100)) === 0;
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                    padding: '6px 8px',
                    background: isValid ? '#f6ffed' : '#fff7e6',
                    borderRadius: 4,
                    border: `1px solid ${isValid ? '#b7eb8f' : '#ffd591'}`,
                  }}
                >
                  <Text style={{ fontSize: 12 }}>
                    Split total: <strong>{fmt(total)}</strong>
                  </Text>
                  <Text
                    style={{
                      fontSize: 12,
                      color: isValid ? '#3f8600' : '#d48806',
                    }}
                  >
                    {isPartialMode
                      ? (isValid ? `✓ Within limit (max ${fmt(target)})` : `Max allowed: ${fmt(target)}`)
                      : (isValid ? '✓ Matches expected total' : `Remaining: ${fmt(remaining)}`)}
                  </Text>
                </div>
              );
            }}
          </Form.Item>

          <Button
            type="dashed"
            onClick={() =>
              add({ mode: defaultMode, amount: null, reference: '' })
            }
            icon={<PlusOutlined />}
            size="small"
            style={{ width: '100%', marginBottom: 4 }}
          >
            Add another payment mode
          </Button>
          <Form.ErrorList errors={errors} />
        </>
      )}
    </Form.List>
  );
};

export default PaymentSplitInput;
export { DEFAULT_PAYMENT_MODES };
